import React, { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import classNames from "classnames";
import { ArrowLeftAlt } from "Components/Icons";
import { ButtonLink } from "Components/ReactstrapSlim";
import { DesignContextProvider } from "Components/DesignContext";
import { useApiClient } from "Services/API/useApiClient";
import { BlogItem, BlogTag, BlogsClient } from "Services/API/API";
import { timeToMilliseconds } from "Utils/date";

import classes from "./ResourcesSection.module.scss";

const MAX_RESOURCES = 3;

const ResourcesSection: React.FC = () => {
   const [resources, setResources] = useState<BlogItem[]>([]);
   const blogClient = useApiClient(BlogsClient);
   const { ref, inView } = useInView({
      triggerOnce: true,
      rootMargin: "500px",
   });

   const getResources = useCallback(
      async (signal?: AbortSignal): Promise<BlogItem[]> => {
         try {
            const blogModel = await blogClient.byTag(
               BlogTag.Homefeature,
               signal
            );
            return blogModel?.items?.slice(0, MAX_RESOURCES) ?? [];
         } catch (err) {
            console.log(err);
            return [];
         }
      },
      [blogClient]
   );

   useEffect(() => {
      let isMounted = true;
      const abortController = new AbortController();

      const fetchResources = async () => {
         const r = await getResources(abortController.signal);
         if (isMounted) {
            setResources(r);
         }
      };

      if (inView) {
         fetchResources();
      }

      return () => {
         isMounted = false;
         abortController.abort();
      };
   }, [getResources, inView]);

   const getReadTime = useCallback((readTime) => {
      const minutes = (readTime ? timeToMilliseconds(readTime) : 0) / 1000 / 60;
      return Math.ceil(minutes) + " min read";
   }, []);

   return (
      <div ref={ref} className={classNames("container", classes.container)}>
         <div className={classes.titleContainer}>
            <h2 className={classes.title}>Resources</h2>
            <div className={classes.subTitle}>
               From remote team collaboration tips to workplace planning
               guidance and flexible workplace case studies, we're here to help
               you navigate the new world of work.
            </div>
         </div>
         <div className={classes.contentContainer}>
            {resources.map((item) => (
               <a key={item.link} href={item.link} className={classes.card}>
                  <img
                     className={classes.cardImage}
                     src={item.imgSource}
                     alt={item.title}
                  />
                  <div className={classes.cardReadTime}>
                     {getReadTime(item.readTime)}
                  </div>
                  <div className={classes.cardTitle}>{item.title}</div>
                  <div className={classes.cardSummary}>{item.summary}</div>
                  <div className={classes.cardAction}>
                     <span className={classes.cardActionLink}>
                        Read more
                        <ArrowLeftAlt className={classes.cardActionIcon} />
                     </span>
                  </div>
               </a>
            ))}
         </div>
         <div className={classes.actionContainer}>
            <DesignContextProvider name="marketing">
               <ButtonLink
                  color="secondary"
                  size="sm"
                  href="https://resources.liquidspace.com/"
               >
                  More posts
               </ButtonLink>
            </DesignContextProvider>
         </div>
      </div>
   );
};

export default ResourcesSection;
